// src/contexts/AppContext.tsx

import React, { createContext, ReactNode, useContext, useState } from "react";
import { Page } from "../pages/cases/types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";

import Translations from "../lang/en/pages.json";

interface Breadcrumb {
  label: string;
  href?: string;
  icon?: ReactNode;
}

interface AppContextType {
  breadcrumbs: Breadcrumb[];
  selectedPage: Page;
  setBreadcrumbs: (breadcrumbs: Breadcrumb[]) => void;
  setSelectedPage: (page: Page) => void;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;

  // new:
  sessionExpired: boolean;
  setSessionExpired: (expired: boolean) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([]);
  const [selectedPage, setSelectedPage] = useState<Page>("Dashboard");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [sessionExpired, setSessionExpired] = useState<boolean>(false);

  const { dialogs: DialogTranslations } = Translations;

  return (
    <AppContext.Provider
      value={{
        breadcrumbs,
        setBreadcrumbs,
        selectedPage,
        setSelectedPage,
        isLoading,
        setIsLoading,

        sessionExpired,
        setSessionExpired,
      }}
    >
      {children}

      {/* Session Expired Dialog */}
      <Dialog
        open={sessionExpired}
        onClose={() => {}}
        aria-labelledby="session-expired-title"
        disableEscapeKeyDown
        hideBackdrop={true}
      >
        <DialogTitle id="session-expired-title">
          {DialogTranslations.session_timeout.title}
        </DialogTitle>
        <DialogContent>
          {DialogTranslations.session_timeout.message}
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            onClick={() => {
              setSessionExpired(false);
              window.location.assign("/auth/login");
            }}
          >
            {DialogTranslations.session_timeout.buttons.login}
          </Button>
        </DialogActions>
      </Dialog>
    </AppContext.Provider>
  );
};

export const useAppContext = (): AppContextType => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};
